import Link from 'next-translate-routes/link';
import React, { useEffect, useState } from 'react';

import { Icon } from '@/components';
import { cn } from '@/utils';

export interface PaginationProps {
  page: number;
  total: number;
  count: number;
  onPageChange: (newPage: number) => void;
  className?: string;
  path?: string;
}

export const DepositsPagination: React.FC<PaginationProps> = ({
  page,
  total,
  count,
  onPageChange,
  className,
  path,
}) => {
  const [currentPage, setCurrentPage] = useState(page);
  const totalPages = Math.ceil(total / count);

  useEffect(() => {
    setCurrentPage(page);
  }, [page]);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumber = Math.ceil(total / count);
    const maxVisiblePages = Math.min(maxPageNumber, 9); // Maximum number of visible page numbers

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(maxPageNumber, startPage + maxVisiblePages - 1);

    // Adjust startPage and endPage if they exceed bounds
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i += 1) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const handlePageChange = (pageNumber: number) => {
    onPageChange(pageNumber);
  };

  const handleGoNext = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  const handleGoPrevious = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  return (
    <div className={cn(className, totalPages === 1 && 'hidden')}>
      <ul className="flex flex-wrap gap-2">
        {currentPage > 1 && (
          <li className="body2 flex size-12 items-center justify-center rounded-full">
            <button type="button" onClick={handleGoPrevious}>
              <Icon name="chevron" className="size-4 -rotate-90" />
            </button>
          </li>
        )}
        {getPageNumbers().map((pageNumber) => (
          <li
            key={pageNumber}
            className="body2 flex size-12 items-center justify-center rounded-full"
          >
            {path ? (
              <Link
                type="button"
                className={cn(
                  'w-full flex items-center justify-center h-full rounded-full',
                  pageNumber === currentPage && 'bg-blue-50 text-blue-500',
                )}
                href={`${path}?page=${pageNumber}`}
                onClick={(e) => {
                  e.preventDefault();
                  handlePageChange(pageNumber);
                }}
              >
                {pageNumber}
              </Link>
            ) : (
              <button
                type="button"
                className={cn(
                  'w-full h-full rounded-full',
                  pageNumber === currentPage && 'bg-blue-50 text-blue-500',
                )}
                disabled={pageNumber === currentPage}
                onClick={() => handlePageChange(pageNumber)}
              >
                {pageNumber}
              </button>
            )}
          </li>
        ))}
        {currentPage < totalPages && (
          <li className="body2 flex size-12 items-center justify-center rounded-full">
            <button type="button" onClick={handleGoNext}>
              <Icon name="chevron" className="size-4 rotate-90" />
            </button>
          </li>
        )}
      </ul>
    </div>
  );
};
